import { createContext, useContext, useEffect, useState } from "react";
import { TraderContextType } from "../types/providers/TraderProvider";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { useFirebase } from "./FirebaseProvider";
import {
	MonthData,
	TraderMonthlyHistory,
	TraderType,
} from "../types/Trader/Trader";
import { useAuth } from "./AuthenticationProvider";
import { useNetwork } from "./NetworkProvider";
import { useSnackbar } from "./SnackbarProvider";

const TraderContext = createContext<TraderContextType>({} as TraderContextType);

export const TraderProvider = ({
	children,
}: {
	children: any;
}): JSX.Element => {
	const { myFS } = useFirebase();
	const { user, traderProfile, setTraderProfile, getTraderInformation } =
		useAuth();
	const { openSnackbar, openLoadingSnackbar, closeLoadingSnackbar } =
		useSnackbar();
	const { makePost } = useNetwork();
	const [tokenMQL4, setTokenMQL4] = useState<string>("");
	const [creditToBePaid, setCreditToBePaid] = useState<number>(0);
	const [monthlyHistory, setMonthlyHistory] =
		useState<TraderMonthlyHistory | null>(null);
	const [currentTraderProfile, setCurrentTraderProfile] =
		useState<TraderType | null>(null);

	const getMQL4Token = async (traderUid: string): Promise<string> => {
		let token = "";
		try {
			try {
				let docRef = doc(myFS, "expertTraderPass", traderUid);
				const docSnap = await getDoc(docRef);
				if (docSnap.exists()) {
					token = docSnap.data().password;
				}
			} catch (err) {
				console.log(err);
			}
		} catch (err) {
			console.log(err);
		}
		setTokenMQL4(token);
		return token;
	};

	const getMonthlyHistory = async (traderUid: string) => {
		let traderMonthlyHistory: TraderMonthlyHistory = { monthlyHistory: [] };
		let creditToBePaid = 0;
		try {
			let collectionRef = collection(
				myFS,
				"traders",
				traderUid,
				"months"
			);
			const snap = await getDocs(collectionRef);
			console.log(snap.empty);
			if (snap.empty) return;
			for (let i = 0; i < snap.docs.length; i++) {
				const monthData = snap.docs[i].data() as MonthData;
				traderMonthlyHistory.monthlyHistory.push({
					...monthData,
					monthId: snap.docs[i].id,
				});
				if (!monthData.paid) {
					creditToBePaid +=
						Math.floor(
							(monthData.elapsedTime / 1000 / 60 / 60) *
								monthData.hourlyRate *
								100000
						) / 100000;
				}
			}
		} catch (err) {
			console.log(err);
		}
		setCreditToBePaid(creditToBePaid);
		setMonthlyHistory(traderMonthlyHistory);
		console.log("traderMonthlyHistory", traderMonthlyHistory);
	};

	useEffect(() => {
		if (traderProfile == null) return;
		setCurrentTraderProfile(traderProfile);
	}, [traderProfile?.uid]);

	const handleAddSubAccount = async (username: string) => {
		if (user == null) return;
		if (traderProfile == null) return;
		if (
			traderProfile.subAccounts != null &&
			traderProfile.subAccounts.length === 10
		) {
			openSnackbar("", "Subaccounts limit reached", "error");
			return;
		}
		try {
			const tokenId = await user.getIdToken();
			const res = await makePost(
				"trader/addSubaccount",
				{ username: username },
				tokenId
			);

			const newTraderInfo = await getTraderInformation(user.uid);
			setTraderProfile(newTraderInfo);
			openSnackbar("", "Subaccount created correctly", "success");
		} catch (err) {
			console.log(err);
			openSnackbar("", "Somethign went wrong", "error");
		}
	};

	const changeCurrentTrader = async (traderUid: string) => {
		openLoadingSnackbar();
		try {
			const traderInfo = await getTraderInformation(traderUid);
			if (traderInfo == null) return;
			await getMonthlyHistory(traderUid);
			await getMQL4Token(traderUid);
			setCurrentTraderProfile(traderInfo);
			openSnackbar("", "Account changed correctly", "success");
		} catch (err) {
			console.log(err);
			openSnackbar("", "Somethign went wrong", "error");
		}
		closeLoadingSnackbar();
	};

	const traderValues: TraderContextType = {
		getMQL4Token,
		getMonthlyHistory,
		changeCurrentTrader,
		handleAddSubAccount,
		currentTraderProfile,
		tokenMQL4,
		monthlyHistory,
		creditToBePaid,
	};

	return (
		<TraderContext.Provider value={traderValues}>
			{children}
		</TraderContext.Provider>
	);
};

export const useTraderProvider = () => useContext(TraderContext);
